import { ODSSpinner } from 'bc-ods';
import { enableMapSet, enablePatches } from 'immer';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { accountStore } from 'stores/useAccountStore';

import clickstream from 'clickstream';
import Routes from './Routes';
import type { AuthAccountWithPassType } from 'api-client/accounts';
import { includeBasePath } from 'utils/url-utils';
import isIframeEmbed from './IframePresentation/isIframeEmbed';
import { ClickstreamEventEnum } from 'bc-model';
import MetricsConfig from './MetricsConfig';

enablePatches();
enableMapSet();

/**
 * EMAIL VERIFICATION
 * If enabled via env var, check that the user has confirmed via the link
 * in their verification email.
 */
const emailNeedsToBeVerified = (user: AuthAccountWithPassType): boolean =>
  !!(user.emailVerificationEnabled && user.emailVerificationRequired && !user.emailVerified);

/**
 * ACCOUNT (user) VERIFICATION
 * If enabled via env var, user needs to wait until approved by an admin
 */
const userNeedsToBeVerified = (user: AuthAccountWithPassType): boolean =>
  !!(user.userVerificationEnabled && user.accountVerifiedStatus !== true);

const dodConsentRequired = (user: AuthAccountWithPassType): boolean => !!user.dodConsentRequired;

const shouldRedirectTo = (user: AuthAccountWithPassType | null): string => {
  if (user == null) return '';
  if (emailNeedsToBeVerified(user)) return '/verify-email';
  if (userNeedsToBeVerified(user)) return '/account-status';
  if (dodConsentRequired(user)) return '/consent';
  return '';
};

const App: FC = () => {
  const location = useLocation();

  const user = accountStore((s) => s.account);
  const loading = accountStore((s) => s.loading);
  const { load: loadUser } = accountStore.getState();

  useEffect(() => {
    if (isIframeEmbed()) {
      return () => {};
    }

    loadUser();

    return () => {
      const { unload } = accountStore.getState();
      unload();
    };
  }, [loadUser]);

  clickstream.track(ClickstreamEventEnum.PAGE_VIEWED);

  if (!isIframeEmbed() && loading.init) {
    return <ODSSpinner />;
  }

  const redirect = shouldRedirectTo(user);

  if (redirect && includeBasePath(redirect).split('?')[0] !== window.location.pathname) {
    return <Navigate to={redirect} replace />;
  }

  return (
    <>
      <MetricsConfig user={user} viewName={location.pathname} />
      <Routes location={location} user={user} loadUser={loadUser} />
    </>
  );
};

export default App;
